import * as React from 'react';
import { IDonorStatus, IDonorRegistration, IListItem } from '@nkr/models/index';
import { Alert, Button, Col, ControlLabel, Form, FormControl, FormGroup, Panel, Row, PanelGroup, HelpBlock } from 'react-bootstrap';
import MaskedInput from 'react-maskedinput';
import DonorDetails from './donor-details';
import { DashRegistrationStep } from '@nkr/enums/index';
import { FormEvent } from 'react';
import { IDashRemoteStatus } from '@nkr/models/donor-status-by-center';
import { IDashWorkFlowStatusRequest } from '@nkr/models/dash-workflow-status-request';

interface IProps {
    centerId: number,
    data: IDonorRegistration,
    statusList: IDonorStatus[],
    remoteStatusList: IDashRemoteStatus[],
    stateList: IListItem[],
    ruleOutReasons?: Array<{ id: number; description: string }>,
    validation: {
        centerWorkFlowIdValid: boolean,
        centerNextFollowupDateValid: boolean
    },
    messages: string[],
    onDataChange: (field: string, value: any) => void,
    onUpdateStatus: ( registration : IDonorRegistration ) => void,
    onSave: () => void,
    onRuleOut?: (donorId: number, reasonId: number, allowReRegistration: boolean, useNkrAssistance: boolean, coordinatorName?: string, phoneNumber?: string, medicalReasonCategory?: number, medicalReasonNotes?: string) => void
}

interface IState {
    list: IDonorRegistration[],
    statusList: IDonorStatus[],
    remoteStatusList: IDashRemoteStatus[],
    fetching: boolean,
    userFilter: number,
    statusFilter: number,
    showFilter: number,
    defaultActiveKey: string,
}

class ViewPreWorkUpData extends React.Component<IProps, IState> {

    readonly state: IState = {
        list: [],
        statusList: [],
        remoteStatusList: [],
        fetching: false,
        userFilter: 0,
        statusFilter: 0,
        showFilter: 0,
        defaultActiveKey: 'key1'
    };

    handleSave = () => {
        this.props.onSave();
    }

    handleStatusUpdate = ( request : IDashWorkFlowStatusRequest ) => {
        // Convert the request back to registration format for the parent component
        const registration: IDonorRegistration = {
            ...this.props.data,
            id: request.registrationId,
            centerId: request.centerId,
            centerWorkflowId: request.statusId,
            centerNextFollowUpDate: request.centerNextFollowUpDate,
            reviewComments: request.reviewComments,
            remoteStatus: request.remoteStatus,
            remoteStatusDate: request.remoteStatusDate,
            onHoldStatusReason: request.onHoldStatusReason
        };
        this.props.onUpdateStatus(registration);
    }

    buildDashRequest = (): IDashWorkFlowStatusRequest => ({
        centerId: this.props.data.centerId,
        registrationId: this.props.data.id,
        statusId: this.props.data.centerWorkflowId || -1,
        centerNextFollowUpDate: this.props.data.centerNextFollowUpDate || null,
        reviewComments: this.props.data.reviewComments || null,
        remoteStatus: this.props.data.remoteStatus,
        onHoldStatusReason: this.props.data.onHoldStatusReason || null,
        remoteStatusDate: this.props.data.remoteStatusDate || null,
        ruleOutReasonId: null,
        allowReRegistration: null,
        useNkrAssistance: null,
        coordinatorName: null,
        phoneNumber: null,
        medicalReasonCategory: null,
        medicalReasonNotes: null
    });

    handleFieldChange = (e: FormEvent<FormControl & HTMLInputElement & HTMLSelectElement>) => {
        let { id, value } = e.currentTarget;
        this.props.onDataChange(id, value);
    };

    handleSelect = (selectedKey: string | null) => {
        this.setState({ defaultActiveKey: selectedKey });
    };


    render = () => {
        if (this.props.data == null)
            return null;

        const { defaultActiveKey } = this.state;
        const disableAllFields = this.props.data.currentRegistrationStep == DashRegistrationStep.Transferred;

        return (
            <div>
                {this.props.data.currentRegistrationStep == DashRegistrationStep.Transferred && (
                    <Alert bsStyle='warning' bsSize='sm'>This donor was transferred; this record is here for visibility only...</Alert>
                )}

                <Alert bsStyle='info' style={{ marginBottom: 10 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <div style={{ width: '70%' }}>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Source:</Col>
                                <Col xs={12} lg={6}>{this.props.data.source}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Center Alias:</Col>
                                <Col xs={12} lg={6}>{this.props.data.centerAlias}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>First Name:</Col>
                                <Col xs={12} lg={6}>{this.props.data.firstName}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Last Name:</Col>
                                <Col xs={12} lg={6}>{this.props.data.lastName}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>DOB:</Col>
                                <Col xs={12} lg={6}>{this.props.data.birthDate}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Email Address:</Col>
                                <Col xs={12} lg={6}>{this.props.data.email}</Col>
                            </Row>
                            <Row>
                                <Col xs={12} lg={6} style={{ fontWeight: 500 }}>Status:</Col>
                                <Col xs={12} lg={6}>{this.props.data.preWorkupLabStatus}</Col>
                            </Row>
                        </div>
                    </div>
                </Alert>

                {this.props.messages.length > 0 && (
                    <Alert bsStyle='danger' bsSize='sm'>
                        The pwl record was NOT saved. Please fix the following errors and try again:
                        <ul>
                            {this.props.messages.map(message => <li>{message}</li>)}
                        </ul>
                    </Alert>
                )}

                <PanelGroup accordion id="accordion-uncontrolled-example" defaultActiveKey={defaultActiveKey}>
                    <Panel eventKey="key1">
                        <Panel.Heading> <Panel.Title toggle style={{ fontSize: '14px' }}>Personal Information</Panel.Title></Panel.Heading>

                        <Panel.Collapse>
                            <Panel.Body>
                                <Form horizontal>
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>First Name:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                id='firstName'
                                                value={this.props.data.firstName}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Last Name:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                id='lastName'
                                                value={this.props.data.lastName}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Date of Birth:</Col>
                                        <Col xs={12} lg={4}>
                                            <MaskedInput
                                                id='birthDate'
                                                className='form-control'
                                                mask='11/11/1111'
                                                placeholder='mm/dd/yyyy'
                                                value={this.props.data.birthDate}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Phone Number:</Col>
                                        <Col xs={12} lg={3}>
                                            <FormControl
                                                id='phoneNumber'
                                                value={this.props.data.phoneNumber}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                    </FormGroup>
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Email:</Col>
                                        <Col xs={12} lg={5}>
                                            <FormControl
                                                id='email'
                                                value={this.props.data.email}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                        <Col xs={12} lg={5} componentClass={ControlLabel}></Col>
                                    </FormGroup>
                                </Form>
                            </Panel.Body>
                        </Panel.Collapse>
                    </Panel>
                    <Panel eventKey="key2">
                        <Panel.Heading> <Panel.Title toggle style={{ fontSize: '14px' }}>Address Information</Panel.Title></Panel.Heading>
                        <Panel.Collapse>
                            <Panel.Body>
                                <Form horizontal>
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Address:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                id='addressLine1'
                                                value={this.props.data.addressLine1}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>                                      
                                    </FormGroup>
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>City:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                id='donorCity'
                                                value={this.props.data.donorCity}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>State:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                componentClass='select'
                                                id='donorStateId'
                                                value={this.props.data.donorStateId}
                                                onChange={this.handleFieldChange}
                                            >
                                                <option value={0}>- Please Select -</option>
                                                {this.props.stateList.map(item =>
                                                    <option key={'st-' + item.id} value={item.id}>{item.description}</option>
                                                )}
                                            </FormControl>
                                        </Col>
                                    </FormGroup>
                                    <FormGroup>
                                        <Col xs={12} lg={2} componentClass={ControlLabel}>Postal Code:</Col>
                                        <Col xs={12} lg={4}>
                                            <FormControl
                                                id='donorPostalCode'
                                                value={this.props.data.donorPostalCode}
                                                onChange={this.handleFieldChange}
                                            />
                                        </Col>
                                        <Col xs={12} lg={6} componentClass={ControlLabel}></Col>
                                    </FormGroup>
                                </Form>
                            </Panel.Body>
                        </Panel.Collapse>
                    </Panel>

                    <Row style={{ marginBottom: '10px' }}>
                        <Col sm={12} md={12}>
                            <Button bsStyle="primary" onClick={() => this.handleSave()} block>
                                Update Record
                            </Button>
                        </Col>
                    </Row>
                    <Row>
                        <Col sm={12} md={12}>
                            <div className='help-block'>
                                <a href={`center/${this.props.centerId}/donors/donor-intake/diagnostics/${this.props.data.id}`} target='_blank'>Edit(Diagnostics)</a>
                            </div>
                        </Col>
                    </Row>
                    <Panel eventKey="key3">
                        <Panel.Heading> <Panel.Title toggle style={{ fontSize: '14px' }}>Donor Details</Panel.Title></Panel.Heading>
                        <Panel.Body collapsible>
                            <DonorDetails
                                data={this.props.data}
                                statusList={this.props.statusList}
                                remoteStatusList={this.props.remoteStatusList}
                                container={this}
                                isMobile={false}
                                disabled={disableAllFields}
                                validation={this.props.validation}
                                onDataChange={this.props.onDataChange}
                                onUpdate={this.handleStatusUpdate}
                                onSaveCenterCard={() => this.handleStatusUpdate(this.buildDashRequest())}
                                onSaveNetworkCard={() => this.handleStatusUpdate(this.buildDashRequest())}
                                ruleOutReasons={this.props.ruleOutReasons}
                                onRuleOut={this.props.onRuleOut}
                            />
                        </Panel.Body>
                    </Panel>
                </PanelGroup>
            </div>
        );
    }
}

export default ViewPreWorkUpData;